import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import './Header.css';
import logoImage from '../../assets/images/S.R.S Kannan.svg';

interface HeaderProps {
  variant?: 'transparent' | 'dark';
}

const Header: React.FC<HeaderProps> = ({ variant }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesHovered, setIsServicesHovered] = useState(false);
  const [isResourcesHovered, setIsResourcesHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesSticky, setIsServicesSticky] = useState(false);
  const [isResourcesSticky, setIsResourcesSticky] = useState(false);

  const servicesNavRef = useRef<HTMLLIElement>(null);
  const resourcesNavRef = useRef<HTMLLIElement>(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Refs for managing hover timeouts
  const servicesTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const resourcesTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (servicesTimeoutRef.current) clearTimeout(servicesTimeoutRef.current);
      if (resourcesTimeoutRef.current) clearTimeout(resourcesTimeoutRef.current);
    };
  }, []);

  // Handle click outside to close sticky menus
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (servicesNavRef.current && !servicesNavRef.current.contains(event.target as Node)) {
        setIsServicesSticky(false);
        setIsServicesHovered(false);
      }
      if (resourcesNavRef.current && !resourcesNavRef.current.contains(event.target as Node)) {
        setIsResourcesSticky(false);
        setIsResourcesHovered(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    document.body.style.overflow = 'unset';
  };



  const services = [
    { id: 'civil-law', title: 'Civil Law' },
    { id: 'property-law', title: 'Property Law' },
    { id: 'criminal-law', title: 'Criminal Law' },
    { id: 'banking-law', title: 'Banking Law' },
    { id: 'consumer-law', title: 'Consumer Law' },
    { id: 'insurance-law', title: 'Insurance Law' },
    { id: 'arbitration-law', title: 'Arbitration Law' },
    { id: 'chit-fund', title: 'Chit Fund Cases' },
    { id: 'debt-recovery-tribunal', title: 'Debt Recovery Tribunal' },
    { id: 'electricity-law', title: 'Electricity Law' },
  ];

  const handleServiceClick = (serviceId: string) => {
    navigate(`/services/${serviceId}`);
    setIsServicesHovered(false);
    closeMenu();
  };

  const handleLinkClick = (path: string) => {
    navigate(path);
    closeMenu();
  };

  // Services dropdown handlers with delayed closing
  const handleServicesMouseEnter = () => {
    if (servicesTimeoutRef.current) {
      clearTimeout(servicesTimeoutRef.current);
      servicesTimeoutRef.current = null;
    }
    setIsServicesHovered(true);
  };

  const handleServicesMouseLeave = () => {
    if (isServicesSticky) return;
    servicesTimeoutRef.current = setTimeout(() => {
      setIsServicesHovered(false);
    }, 250); // 250ms delay before closing
  };

  const handleServicesClick = () => {
    navigate('/services');
    if (isServicesSticky) {
      setIsServicesSticky(false);
      setIsServicesHovered(false);
    } else {
      setIsServicesSticky(true);
      setIsServicesHovered(true);
      // Close others
      setIsResourcesSticky(false);
      setIsResourcesHovered(false);
    }
  };

  // Resources dropdown handlers with delayed closing
  const handleResourcesMouseEnter = () => {
    if (resourcesTimeoutRef.current) {
      clearTimeout(resourcesTimeoutRef.current);
      resourcesTimeoutRef.current = null;
    }
    setIsResourcesHovered(true);
  };

  const handleResourcesMouseLeave = () => {
    if (isResourcesSticky) return;
    resourcesTimeoutRef.current = setTimeout(() => {
      setIsResourcesHovered(false);
    }, 250); // 250ms delay before closing
  };

  // const handleResourcesClick = () => {
  //   if (isResourcesSticky) {
  //     setIsResourcesSticky(false);
  //     setIsResourcesHovered(false);
  //   } else {
  //     setIsResourcesSticky(true);
  //     setIsResourcesHovered(true);
  //     // Close others
  //     setIsServicesSticky(false);
  //     setIsServicesHovered(false);
  //   }
  // };

  return (
    <header className={`header ${variant === 'dark' ? 'header-dark' : ''}`}>
      {/* Top Bar */}
      <div className="top-bar">
        <div className="container">
          <div className="top-bar-content">
            <div className="top-bar-left">
              <span className="top-bar-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <a href="https://maps.app.goo.gl/hUJCH5F27bfJSo9G9" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                  S-2, 9/91 Sri Vari Complex, Grey Town, Sri Kant EXTN, Coimbatore -641018
                </a>
              </span>
              <span className="top-bar-separator">|</span>
              <span className="top-bar-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                  <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                </svg>
                <a href="mailto:advocate@gmail.com">skannansrs@gmail.com</a>
              </span>
              <span className="top-bar-separator">|</span>
              <span className="top-bar-item">Mon - Sat: 9am - 6pm</span>
            </div>

            <div className="top-bar-right">
              {['facebook', 'twitter', 'instagram', 'youtube'].map((platform) => (
                <a
                  key={platform}
                  href={`https://${platform}.com`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={platform}
                  className="social-icon"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    {platform === 'facebook' && (
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    )}
                    {platform === 'twitter' && (
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                    )}
                    {platform === 'instagram' && (
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    )}
                  </svg>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Bar */}
      <nav className={`navbar ${isScrolled ? 'scrolled' : ''} ${(variant === 'dark' && !isScrolled) ? 'solid' : ''}`}>
        <div className="container">
          <div className="navbar-content">
            <div className="navbar-logo" onClick={() => navigate('/')}>
              <img src={logoImage} alt="Advocate Kannan" className="logo-icon" style={{ width: '180px', height: 'auto' }} />
            </div>

            {/* Desktop Menu */}
            <ul className="navbar-menu desktop-only">
              <li>
                <div className={`nav-link ${location.pathname === '/' ? 'active' : ''}`} onClick={() => handleLinkClick('/')}>Home</div>
              </li>
              <li>
                <div className={`nav-link ${location.pathname === '/about' ? 'active' : ''}`} onClick={() => handleLinkClick('/about')}>About Us</div>
              </li>
              <li
                ref={servicesNavRef}
                className={`nav-item-with-dropdown ${isServicesHovered ? 'hovered' : ''}`}
                onMouseEnter={handleServicesMouseEnter}
                onMouseLeave={handleServicesMouseLeave}
              >
                <div
                  className={`nav-link ${location.pathname.startsWith('/services') ? 'active' : ''}`}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                  onClick={handleServicesClick}
                >
                  Services.<span className="dropdown-arrow">▾</span>
                </div>
                {isServicesHovered && (
                  <div
                    className="mega-menu"
                    onMouseEnter={handleServicesMouseEnter}
                    onMouseLeave={handleServicesMouseLeave}
                  >
                    <div className="mega-menu-container">
                      <div className="mega-menu-sidebar">
                        <div className="sidebar-item active">
                          <span>Services</span>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 18l6-6-6-6" />
                          </svg>
                        </div>
                      </div>
                      <div className="mega-menu-content">
                        <div className="mega-menu-grid">
                          <div className="mega-menu-column">
                            {services.slice(0, 4).map((service) => (
                              <div key={service.id} className="mega-menu-link" onClick={() => handleServiceClick(service.id)}>
                                {service.title}
                              </div>
                            ))}
                          </div>
                          <div className="mega-menu-column">
                            {services.slice(4, 7).map((service) => (
                              <div key={service.id} className="mega-menu-link" onClick={() => handleServiceClick(service.id)}>
                                {service.title}
                              </div>
                            ))}
                          </div>
                          <div className="mega-menu-column">
                            {services.slice(7).map((service) => (
                              <div key={service.id} className="mega-menu-link" onClick={() => handleServiceClick(service.id)}>
                                {service.title}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </li>
              <li
                ref={resourcesNavRef}
                className={`nav-item-with-dropdown ${isResourcesHovered ? 'hovered' : ''}`}
                onMouseEnter={handleResourcesMouseEnter}
                onMouseLeave={handleResourcesMouseLeave}
              >
                <div
                  className={`nav-link ${(location.pathname.includes('/blog') || location.pathname.includes('/faqs')) ? 'active' : ''}`}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                  onClick={() => handleLinkClick('/blog')}
                >
                  Resources.<span className="dropdown-arrow">▾</span>
                </div>
                {isResourcesHovered && (
                  <div
                    className="mega-menu"
                    onMouseEnter={handleResourcesMouseEnter}
                    onMouseLeave={handleResourcesMouseLeave}
                  >
                    <div className="mega-menu-container">
                      <div className="mega-menu-sidebar">
                        <div className="sidebar-item active">
                          <span>Resources</span>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 18l6-6-6-6" />
                          </svg>
                        </div>
                      </div>
                      <div className="mega-menu-content">
                        <div className="mega-menu-grid">
                          <div className="mega-menu-column">
                            <div className="mega-menu-link" onClick={() => handleLinkClick('/blog')}>Blog</div>
                            <div className="mega-menu-link" onClick={() => handleLinkClick('/faqs')}>FAQs</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </li>
              <li>
                <div className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`} onClick={() => handleLinkClick('/contact')}>Contact Us</div>
              </li>
            </ul>

            <div className="header-actions">
              <Link to="/contact" className="consultation-btn desktop-only">
                <b>Free Consultation</b>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="btn-icon">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polygon points="10 8 16 12 10 16 10 8" fill="currentColor"></polygon>
                </svg>
              </Link>

              {/* Mobile Menu Toggle (Visible on small screens) */}
              <button className={`menu-toggle ${isMenuOpen ? 'active' : ''}`} onClick={toggleMenu} aria-label="Toggle Menu">
                <span className="hamburger"></span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Side Drawer (Mobile Sidebar) */}
      <div className={`side-drawer ${isMenuOpen ? 'open' : ''}`}>
        <div className="drawer-overlay" onClick={closeMenu}></div>
        <div className="drawer-content">
          <div className="drawer-header">
            <div className="navbar-logo">
              <img src={logoImage} alt="Advocate Kannan" className="logo-icon" style={{ width: '150px', height: 'auto' }} />
            </div>
            <button className="close-btn" onClick={closeMenu}>&times;</button>
          </div>

          <ul className="drawer-links">
            <li onClick={() => handleLinkClick('/')}>
              <svg className="drawer-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
              Home
            </li>
            <li onClick={() => handleLinkClick('/about')}>
              <svg className="drawer-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
              About Us
            </li>

            <li className="drawer-section">
              <span className="section-label">
                <svg className="drawer-icon-small" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="7" height="7" x="3" y="3" rx="1" /><rect width="7" height="7" x="14" y="3" rx="1" /><rect width="7" height="7" x="14" y="14" rx="1" /><rect width="7" height="7" x="3" y="14" rx="1" /></svg>
                Our Services
              </span>
              <div className="drawer-sublinks-container">
                {services.map(s => (
                  <div key={s.id} className="drawer-sub-link" onClick={() => handleServiceClick(s.id)}>
                    {s.title}
                  </div>
                ))}
              </div>
            </li>

            <li className="drawer-section">
              <span className="section-label">
                <svg className="drawer-icon-small" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1-2.5-2.5Z" /><path d="M8 7h6" /><path d="M8 11h8" /></svg>
                Resources
              </span>
              <div className="drawer-sublinks-container">
                <div className="drawer-sub-link" onClick={() => handleLinkClick('/blog')}>Blog</div>
                <div className="drawer-sub-link" onClick={() => handleLinkClick('/faqs')}>FAQs</div>
              </div>
            </li>

            <li onClick={() => handleLinkClick('/contact')}>
              <svg className="drawer-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
              Contact Us
            </li>
          </ul>

          <div className="drawer-footer">
            <Link to="/contact" className="consultation-btn" style={{ width: '100%', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', textDecoration: 'none' }} onClick={closeMenu}>
              Free Consultation
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="btn-icon">
                <circle cx="12" cy="12" r="10"></circle>
                <polygon points="10 8 16 12 10 16 10 8" fill="currentColor"></polygon>
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
